// Settings → Account. GDPR data export + account closure (Phase 3).
//
// Endpoints (modules/portal_account.js, all requirePortal-authed):
//   GET  /api/portal/account/status     — closure lifecycle
//   GET  /api/portal/account/export      — full JSON data export (download)
//   GET  /api/portal/account/export?format=csv — customers CSV (download)
//   POST /api/portal/account/close       — { confirmName } schedules closure
//   POST /api/portal/account/reactivate  — cancels a scheduled closure

import { ApiError } from "@tkos/api-client";
import type { SettingsTab, TabCleanup } from "../types";
import type { PageContext } from "../../../shell/types";

interface AccountStatus {
  ok: boolean;
  businessName?: string | null;
  deletionRequestedAt?: string | null;
  deletionScheduledAt?: string | null;
  closedAt?: string | null;
}

function esc(s: unknown): string {
  return String(s == null ? "" : s).replace(/[<>&"]/g, (c) =>
    ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c] ?? c),
  );
}

const account: SettingsTab = {
  id: "account",
  label: "Account",
  mount(container: HTMLElement, ctx: PageContext): TabCleanup {
    const ac = new AbortController();
    container.innerHTML = `<div class="set-account"><p style="color:var(--color-text-secondary)">Loading…</p></div>`;
    const root = container.querySelector<HTMLDivElement>(".set-account")!;

    function render(status: AccountStatus): void {
      const scheduled = status.deletionScheduledAt ? new Date(status.deletionScheduledAt) : null;
      const bn = status.businessName ?? "";
      root.innerHTML = `
        <section style="margin-bottom:28px">
          <h3 style="margin:0 0 6px">Your data</h3>
          <p style="margin:0 0 12px;color:var(--color-text-secondary)">Download a copy of your data — customers, quotes, payments and messages. We recommend doing this before closing your account.</p>
          <div style="display:flex;gap:10px;flex-wrap:wrap">
            <a class="set-btn" href="/api/portal/account/export" download>Download all (JSON)</a>
            <a class="set-btn" href="/api/portal/account/export?format=csv" download>Customers (CSV)</a>
          </div>
        </section>
        ${scheduled
          ? `
        <section style="border:1px solid var(--color-amber,#F59E0B);border-radius:12px;padding:16px">
          <h3 style="margin:0 0 6px">Account closure scheduled</h3>
          <p style="margin:0 0 12px;color:var(--color-text-secondary)">Your account and all data will be permanently deleted on <strong>${esc(scheduled.toDateString())}</strong>. You can reactivate any time before then by staying signed in.</p>
          <button type="button" class="set-btn" data-role="reactivate">Reactivate account</button>
        </section>`
          : `
        <section style="border:1px solid #ef444455;border-radius:12px;padding:16px">
          <h3 style="margin:0 0 6px">Close account</h3>
          <p style="margin:0 0 12px;color:var(--color-text-secondary)">Closing your account schedules permanent deletion of all your data after a <strong>30-day grace period</strong>. You can reactivate any time during those 30 days.</p>
          <label style="display:block;margin:0 0 12px">
            <span style="display:block;margin-bottom:6px;font-size:0.85rem">Type your business name <strong>${esc(bn)}</strong> to confirm</span>
            <input type="text" data-role="confirm-name" placeholder="${esc(bn)}" autocomplete="off"
                   style="width:100%;max-width:360px;padding:8px 10px;border:1px solid var(--color-border);border-radius:8px;background:var(--color-input-bg);color:var(--color-text-primary)" />
          </label>
          <button type="button" class="set-btn" data-role="close-account" disabled
                  style="background:#ef4444;border-color:#ef4444;color:#fff;opacity:.55">Close account</button>
        </section>`}
      `;
      wire(status);
    }

    function wire(status: AccountStatus): void {
      const reBtn = root.querySelector<HTMLButtonElement>('[data-role="reactivate"]');
      reBtn?.addEventListener("click", () => {
        void (async () => {
          reBtn.disabled = true;
          try {
            await ctx.api<unknown>("/portal/account/reactivate", { method: "POST" });
            ctx.showToast("Account reactivated — closure cancelled.", "success");
            await load();
          } catch {
            ctx.showToast("Could not reactivate the account.", "error");
            reBtn.disabled = false;
          }
        })();
      });

      const input = root.querySelector<HTMLInputElement>('[data-role="confirm-name"]');
      const closeBtn = root.querySelector<HTMLButtonElement>('[data-role="close-account"]');
      const expected = (status.businessName ?? "").trim().toLowerCase();
      input?.addEventListener("input", () => {
        if (!closeBtn) return;
        const match = input.value.trim().toLowerCase() === expected && expected !== "";
        closeBtn.disabled = !match;
        closeBtn.style.opacity = match ? "1" : ".55";
      });
      closeBtn?.addEventListener("click", () => {
        void (async () => {
          const name = input?.value.trim() ?? "";
          closeBtn.disabled = true;
          try {
            await ctx.api<unknown>("/portal/account/close", {
              method: "POST",
              body: JSON.stringify({ confirmName: name }),
            });
            ctx.showToast("Account closure scheduled — we've emailed you the details.", "success");
            await load();
          } catch (err) {
            const msg = err instanceof ApiError && err.status === 400
              ? "The name you typed doesn't match your business name."
              : "Could not close the account — please try again.";
            ctx.showToast(msg, "error");
            closeBtn.disabled = false;
          }
        })();
      });
    }

    async function load(): Promise<void> {
      try {
        const s = await ctx.api<AccountStatus>("/portal/account/status", { signal: ac.signal });
        render(s);
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        root.innerHTML = `<p style="color:var(--color-text-secondary)">Couldn't load account settings.</p>`;
      }
    }
    void load();

    return () => ac.abort();
  },
};

export default account;
